import { useState, useMemo, useEffect } from "react";
import { useAuthContext } from "@/contexts/AuthContext";
import { useUnifiedMatches, MatchSource } from "@/hooks/useUnifiedMatches";
import { useChats } from "@/hooks/useChat";
import { useMatchPresence } from "@/hooks/useMatchPresence";
import { useUnreadMessages } from "@/hooks/useUnreadMessages";
import { useMatchCompatibility } from "@/hooks/useMatchCompatibility";
import { Globe, Sparkles, Gamepad2, Clock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import ChatModal from "@/components/chat/ChatModal";
import { usePortal } from "@/hooks/usePortal";
import { useGlobalConnect } from "@/hooks/useGlobalConnect";
import { cn } from "@/lib/utils";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { MatchCard, getContextDisplay } from "@/components/matches/MatchCard";
import { MatchesEmptyState } from "@/components/matches/MatchesEmptyState";
import { MatchesHeader } from "@/components/matches/MatchesHeader";
import { ContinueVibeNudge } from "@/components/matches/ContinueVibeNudge";
import { ConnectionWeatherBanner } from "@/components/matches/ConnectionWeatherBanner";
import { SuggestedNextStep } from "@/components/matches/SuggestedNextStep";
import { SparksTimeline } from "@/components/matches/SparksTimeline";
import { VirtualDateInvite } from "@/components/virtual-date/VirtualDateInvite";
import { VirtualDateRoom } from "@/components/virtual-date/VirtualDateRoom";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

const MatchesPage = () => {
  const { user } = useAuthContext();
  const navigate = useNavigate();
  const { matches, loading, sourceFilter, setSourceFilter, stats, allMatches } = useUnifiedMatches();
  const { chats } = useChats();
  const { getPortalWithUser } = usePortal();
  const { settings: globalConnectSettings } = useGlobalConnect();
  
  const [selectedChat, setSelectedChat] = useState<{
    chatId: string;
    otherUser: { id: string; name: string; photo_url: string | null };
  } | null>(null);
  const [dismissedNudges, setDismissedNudges] = useState<Set<string>>(new Set());
  const [virtualDateTarget, setVirtualDateTarget] = useState<{
    id: string;
    name: string;
    photo_url: string | null;
  } | null>(null);
  const [activeVirtualDateRoomId, setActiveVirtualDateRoomId] = useState<string | null>(null);
  const [portalModes, setPortalModes] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<"matches" | "timeline">("matches");

  // Get all matched user IDs for hooks
  const matchedUserIds = useMemo(
    () => matches.map((m) => m.matchedProfile?.id).filter(Boolean) as string[],
    [matches]
  );

  // Get match-user pairs for unread count lookup
  const matchUserPairs = useMemo(
    () =>
      matches
        .filter((m) => m.matchedProfile)
        .map((m) => ({
          matchId: m.id,
          otherUserId: m.matchedProfile!.id,
        })),
    [matches]
  );

  // Use presence, unread, and compatibility hooks
  const { isOnline, getLastSeen } = useMatchPresence(matchedUserIds);
  const { getUnreadCount } = useUnreadMessages(matchUserPairs);
  const { getCompatibility } = useMatchCompatibility(matchedUserIds);

  // Enrich matches with all data
  const enrichedMatches = useMemo(() => {
    return matches.map((match) => {
      const matchedUserId = match.matchedProfile?.id;
      const chat = chats.find(
        (c) =>
          (c.user1_id === user?.id && c.user2_id === matchedUserId) ||
          (c.user2_id === user?.id && c.user1_id === matchedUserId)
      );

      return {
        ...match,
        lastMessage: chat?.lastMessage?.content || null,
        unreadCount: getUnreadCount(match.id),
        isOnline: matchedUserId ? isOnline(matchedUserId) : false,
        lastSeen: matchedUserId ? getLastSeen(matchedUserId) : null,
        compatibility: matchedUserId ? getCompatibility(matchedUserId) : undefined,
        chatId: chat?.id,
      };
    });
  }, [matches, chats, user?.id, isOnline, getLastSeen, getUnreadCount, getCompatibility]);

  // Find recent exhibition/global match for nudge
  const recentSpecialMatch = useMemo(() => {
    const specialMatches = enrichedMatches.filter(
      (m) =>
        (m.match_source === "exhibition" || m.match_source === "global_connect") &&
        !dismissedNudges.has(m.id)
    );
    // Return most recent one
    return specialMatches[0] || null;
  }, [enrichedMatches, dismissedNudges]);

  // Fetch portal modes for gradient background
  useEffect(() => {
    if (!user) return;
    
    const fetchPortalModes = async () => {
      const { data } = await supabase
        .from("portals")
        .select("mode")
        .or(`user_a_id.eq.${user.id},user_b_id.eq.${user.id}`)
        .eq("status", "active");
      
      if (data) {
        setPortalModes(data.map((p) => p.mode));
      }
    };
    
    fetchPortalModes();
  }, [user]);

  // Determine gradient colors based on intents/modes
  const gradientColors = useMemo(() => {
    const intents: string[] = [];
    
    // Add Global Connect intent
    if (globalConnectSettings?.intent_level) {
      intents.push(globalConnectSettings.intent_level);
    }
    
    // Add Portal modes
    portalModes.forEach((mode) => intents.push(mode));
    
    // Default gradient
    if (intents.length === 0) {
      return "from-pink-500/10 via-blue-500/5 to-purple-500/10";
    }
    
    // Build gradient based on intents
    const colors: string[] = [];
    if (intents.includes("serious") || intents.includes("dating")) {
      colors.push("from-blue-500/10");
    }
    if (intents.includes("curious") || intents.includes("casual")) {
      colors.push("via-pink-500/5");
    }
    if (intents.includes("deep") || intents.includes("anonymous")) {
      colors.push("to-purple-500/10");
    }
    
    return colors.length > 0 
      ? colors.join(" ") 
      : "from-pink-500/10 via-blue-500/5 to-purple-500/10";
  }, [globalConnectSettings, portalModes]);

  const openChat = (match: typeof enrichedMatches[0]) => {
    if (match.chatId && match.matchedProfile) {
      setSelectedChat({
        chatId: match.chatId,
        otherUser: {
          id: match.matchedProfile.id,
          name: match.matchedProfile.name,
          photo_url: match.matchedProfile.photo_url,
        },
      });
    }
  };

  const handleOpenPortal = async (profile: { id: string; name: string }) => {
    const portal = await getPortalWithUser(profile.id);
    if (portal) {
      navigate(`/portal?open=${portal.id}`);
    } else {
      navigate(`/portal?create=${profile.id}&name=${encodeURIComponent(profile.name)}`);
    }
  };

  const handleVideoChat = (match: typeof enrichedMatches[0]) => {
    if (match.chatId && match.matchedProfile) {
      // Open chat and trigger video call
      setSelectedChat({
        chatId: match.chatId,
        otherUser: {
          id: match.matchedProfile.id,
          name: match.matchedProfile.name,
          photo_url: match.matchedProfile.photo_url,
        },
      });
      // TODO: Auto-trigger video call after modal opens
      toast.success(`Starting video chat with ${match.matchedProfile.name}...`);
    }
  };

  const handleVirtualDate = (match: typeof enrichedMatches[0]) => {
    if (match.matchedProfile) {
      setVirtualDateTarget({
        id: match.matchedProfile.id,
        name: match.matchedProfile.name,
        photo_url: match.matchedProfile.photo_url,
      });
    }
  };

  const handleTabChange = (value: string) => {
    setSourceFilter(value as MatchSource);
  };

  if (loading) {
    return (
      <div className="min-h-screen sky-gradient pb-20">
        <MatchesHeader stats={{ total: 0, exhibition: 0, popBalloon: 0, globalConnect: 0 }} />
        <div className="p-4 space-y-3">
          {[1, 2, 3, 4].map((i) => (
            <div 
              key={i} 
              className="flex items-center gap-4 p-4 bg-card/50 rounded-2xl animate-pulse"
              style={{ animationDelay: `${i * 0.1}s` }}
            >
              <div className="w-16 h-16 rounded-full bg-muted/60 shimmer-bg" />
              <div className="flex-1 space-y-2">
                <div className="h-5 w-2/3 bg-muted/60 rounded" />
                <div className="h-3 w-1/2 bg-muted/40 rounded" />
                <div className="h-5 w-20 bg-muted/30 rounded-full" />
              </div>
              <div className="w-12 h-12 rounded-full bg-muted/40" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24 relative overflow-hidden">
      {/* Breathing gradient background */}
      <div 
        className={cn(
          "fixed inset-0 bg-gradient-to-br animate-pulse",
          gradientColors,
          "opacity-60"
        )}
        style={{
          animation: "breathing 4s ease-in-out infinite",
        }}
      />
      <style>{`
        @keyframes breathing {
          0%, 100% { opacity: 0.6; }
          50% { opacity: 0.8; }
        }
      `}</style>
      
      <div className="relative z-10">
        <MatchesHeader stats={stats} />

        <div className="p-4">
          {/* Connection Weather Banner */}
          {enrichedMatches.length > 0 && (
            <ConnectionWeatherBanner matches={enrichedMatches} className="mb-4" />
          )}

          {/* Suggested Next Step Carousel */}
          {enrichedMatches.length > 0 && (
            <SuggestedNextStep
              matches={enrichedMatches}
              onVideoChat={(matchId) => {
                const match = enrichedMatches.find((m) => m.id === matchId);
                if (match) handleVideoChat(match);
              }}
              onMessage={(matchId) => {
                const match = enrichedMatches.find((m) => m.id === matchId);
                if (match) openChat(match);
              }}
              onPortal={(matchId) => {
                const match = enrichedMatches.find((m) => m.id === matchId);
                if (match?.matchedProfile) {
                  handleOpenPortal({
                    id: match.matchedProfile.id,
                    name: match.matchedProfile.name,
                  });
                }
              }}
              className="mb-4"
            />
          )}

          {/* Continue the Vibe Nudge */}
          {recentSpecialMatch && recentSpecialMatch.matchedProfile && (
            <ContinueVibeNudge
              matchSource={recentSpecialMatch.match_source}
              profileName={recentSpecialMatch.matchedProfile.name}
              contextDisplay={getContextDisplay(recentSpecialMatch)}
              onVideoChat={() => handleVideoChat(recentSpecialMatch)}
              onDismiss={() => {
                setDismissedNudges((prev) => new Set([...prev, recentSpecialMatch.id]));
              }}
              className="mb-4"
            />
          )}

          {/* Tabs for filtering by source and timeline */}
          <Tabs value={activeTab === "timeline" ? "timeline" : sourceFilter} onValueChange={(value) => {
            if (value === "timeline") {
              setActiveTab("timeline");
            } else {
              setActiveTab("matches");
              handleTabChange(value);
            }
          }} className="w-full">
            <TabsList className="w-full grid grid-cols-5 mb-4 bg-muted/50 p-1 rounded-xl">
            <TabsTrigger 
              value="all" 
              className={cn(
                "text-xs sm:text-sm rounded-lg transition-all",
                sourceFilter === "all" && "shadow-sm"
              )}
            >
              All
            </TabsTrigger>
            <TabsTrigger 
              value="exhibition" 
              className={cn(
                "text-xs sm:text-sm rounded-lg transition-all",
                sourceFilter === "exhibition" && "shadow-sm bg-amber-500/10"
              )}
            >
              <Sparkles className="w-3 h-3 mr-1 hidden sm:inline" />
              <span className="hidden sm:inline">Live Dating Event</span>
              <span className="sm:hidden">🎭</span>
            </TabsTrigger>
            <TabsTrigger 
              value="pop_balloon" 
              className={cn(
                "text-xs sm:text-sm rounded-lg transition-all",
                sourceFilter === "pop_balloon" && "shadow-sm bg-pink-500/10"
              )}
            >
              <span className="mr-1 hidden sm:inline">🎈</span>
              <span className="hidden sm:inline">Pop Balloon</span>
              <span className="sm:hidden">🎈</span>
            </TabsTrigger>
            <TabsTrigger 
              value="global_connect" 
              className={cn(
                "text-xs sm:text-sm rounded-lg transition-all",
                sourceFilter === "global_connect" && "shadow-sm bg-blue-500/10"
              )}
            >
              <Globe className="w-3 h-3 mr-1 hidden sm:inline" />
              <span className="hidden sm:inline">Global Connect</span>
              <span className="sm:hidden">🌍</span>
            </TabsTrigger>
            <TabsTrigger 
              value="timeline" 
              className={cn(
                "text-xs sm:text-sm rounded-lg transition-all",
                activeTab === "timeline" && "shadow-sm bg-purple-500/10"
              )}
            >
              <Clock className="w-3 h-3 mr-1 hidden sm:inline" />
              <span className="hidden sm:inline">My Sparks</span>
              <span className="sm:hidden">✨</span>
            </TabsTrigger>
          </TabsList>

          {/* Timeline Tab */}
          <TabsContent value="timeline" className="mt-0">
            <SparksTimeline
              matches={allMatches}
              onMatchClick={(matchId) => {
                const match = enrichedMatches.find((m) => m.id === matchId);
                if (match) openChat(match);
              }}
            />
          </TabsContent>

          {/* Matches Tabs */}
          <TabsContent value={sourceFilter} className="mt-0">
            {enrichedMatches.length === 0 ? (
              <MatchesEmptyState filter={sourceFilter} />
            ) : (
              <div className="space-y-3">
                {enrichedMatches.map((match, index) => (
                  <MatchCard
                    key={match.id}
                    match={match}
                    index={index}
                    onOpenChat={() => openChat(match)}
                    onOpenPortal={() => {
                      if (match.matchedProfile) {
                        handleOpenPortal({ 
                          id: match.matchedProfile.id, 
                          name: match.matchedProfile.name 
                        });
                      }
                    }}
                    onVideoChat={() => handleVideoChat(match)}
                    onVirtualDate={() => handleVirtualDate(match)}
                  />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
        </div>
      </div>

      {/* Chat Modal */}
      {selectedChat && (
        <ChatModal
          chatId={selectedChat.chatId}
          otherUser={selectedChat.otherUser}
          isOpen={!!selectedChat}
          onClose={() => setSelectedChat(null)}
        />
      )}

      {/* Virtual Date Invite Modal */}
      {virtualDateTarget && (
        <VirtualDateInvite
          isOpen={!!virtualDateTarget}
          onClose={() => setVirtualDateTarget(null)}
          partnerId={virtualDateTarget.id}
          partnerName={virtualDateTarget.name}
          partnerPhoto={virtualDateTarget.photo_url}
          onRoomCreated={(roomId) => {
            setVirtualDateTarget(null);
            setActiveVirtualDateRoomId(roomId);
          }}
        />
      )}

      {/* Active Virtual Date Room */}
      {activeVirtualDateRoomId && (
        <VirtualDateRoom
          roomId={activeVirtualDateRoomId}
          onClose={() => setActiveVirtualDateRoomId(null)}
        />
      )}
    </div>
  );
};

export default MatchesPage;
